import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Alert,
} from '@/components/ui'
import { api } from '@/api/client'
import { Loader2, Key, Trash2, Check, CreditCard, ExternalLink, Copy, CheckCircle } from 'lucide-react'

interface StripeConfig {
  enabled: boolean
  publishable_key: string | null
  connect_account_id: string | null
  configured: boolean
}

interface PayPalConfig {
  enabled: boolean
  client_id: string | null
  merchant_id: string | null
  configured: boolean
}

interface OrgConfigResponse {
  stripe: StripeConfig
  paypal: PayPalConfig
}

interface StripeSetupResult {
  webhook_url: string
  required_events: string[]
}

export function PaymentsSettingsPage() {
  const { orgSlug } = useParams()
  const [loading, setLoading] = useState(true)
  const [config, setConfig] = useState<OrgConfigResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingStripe, setEditingStripe] = useState(false)
  const [saving, setSaving] = useState(false)
  const [setupResult, setSetupResult] = useState<StripeSetupResult | null>(null)
  const [copiedUrl, setCopiedUrl] = useState(false)

  // Stripe form state
  const [publishableKey, setPublishableKey] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')

  useEffect(() => {
    loadConfig()
  }, [orgSlug])

  const loadConfig = async () => {
    if (!orgSlug) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.orgs.getConfig(orgSlug)
      setConfig(response)
    } catch (err) {
      console.error('Failed to load config:', err)
      setError('Failed to load payment settings')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStripe = () => {
    setPublishableKey(config?.stripe?.publishable_key || '')
    setSecretKey('')
    setWebhookSecret('')
    setEditingStripe(true)
  }

  const handleCancelStripe = () => {
    setEditingStripe(false)
    setPublishableKey('')
    setSecretKey('')
    setWebhookSecret('')
  }

  const handleSaveStripe = async () => {
    if (!orgSlug) return

    if (!webhookSecret) {
      setError('Webhook secret is required for payment processing')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const result = await api.orgs.setStripeKeys(orgSlug, {
        publishable_key: publishableKey,
        secret_key: secretKey,
        webhook_secret: webhookSecret,
      })
      setSetupResult({
        webhook_url: result.webhook_url,
        required_events: result.required_events,
      })
      await loadConfig()
      setEditingStripe(false)
      setPublishableKey('')
      setSecretKey('')
      setWebhookSecret('')
    } catch (err) {
      console.error('Failed to save Stripe keys:', err)
      setError('Failed to save Stripe configuration')
    } finally {
      setSaving(false)
    }
  }

  const copyWebhookUrl = async () => {
    if (setupResult?.webhook_url) {
      await navigator.clipboard.writeText(setupResult.webhook_url)
      setCopiedUrl(true)
      setTimeout(() => setCopiedUrl(false), 2000)
    }
  }

  const handleRemoveStripe = async () => {
    if (!orgSlug) return
    if (!confirm('Are you sure you want to remove your Stripe configuration? This will disable payments.')) {
      return
    }

    try {
      await api.orgs.removeStripeKeys(orgSlug)
      await loadConfig()
    } catch (err) {
      console.error('Failed to remove Stripe keys:', err)
      setError('Failed to remove Stripe configuration')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--text-secondary)]" />
      </div>
    )
  }

  const stripeConfigured = config?.stripe?.configured || false

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">
          Payment Providers
        </h2>
        <p className="text-sm text-[var(--text-secondary)]">
          Configure payment processing for your organization. This enables billing and subscription features for your users.
        </p>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stripe Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#635bff]/10 flex items-center justify-center">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#635bff">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-base">Stripe</CardTitle>
                <CardDescription>
                  {stripeConfigured ? (
                    <span className="flex items-center gap-1 text-green-500">
                      <Check className="w-3 h-3" />
                      Configured
                    </span>
                  ) : (
                    'Not configured'
                  )}
                </CardDescription>
              </div>
            </div>
            {!editingStripe && (
              <div className="flex items-center gap-2">
                {stripeConfigured && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveStripe}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleEditStripe}
                >
                  <Key className="w-4 h-4 mr-1" />
                  {stripeConfigured ? 'Update' : 'Configure'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        {editingStripe && (
          <CardContent className="pt-0">
            <div className="space-y-4 pt-4 border-t border-[var(--border-subtle)]">
              <div className="space-y-2">
                <Label>Publishable Key</Label>
                <Input
                  value={publishableKey}
                  onChange={(e) => setPublishableKey(e.target.value)}
                  placeholder="pk_live_..."
                />
                <p className="text-xs text-[var(--text-muted)]">
                  Your Stripe publishable key (starts with pk_)
                </p>
              </div>
              <div className="space-y-2">
                <Label>Secret Key</Label>
                <Input
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={stripeConfigured ? '••••••••••••••••' : 'sk_live_...'}
                />
                <p className="text-xs text-[var(--text-muted)]">
                  {stripeConfigured
                    ? 'Leave blank to keep the existing secret key'
                    : 'Your Stripe secret key (starts with sk_)'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Webhook Secret <span className="text-red-500">*</span></Label>
                <Input
                  type="password"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={stripeConfigured ? '••••••••••••••••' : 'whsec_...'}
                />
                <p className="text-xs text-[var(--text-muted)]">
                  Required for verifying payment webhooks (starts with whsec_)
                </p>
              </div>
              <div className="flex items-center justify-between">
                <a
                  href="https://dashboard.stripe.com/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--accent)] hover:underline flex items-center gap-1"
                >
                  Get API keys from Stripe
                  <ExternalLink className="w-3 h-3" />
                </a>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" onClick={handleCancelStripe} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveStripe}
                    loading={saving}
                    disabled={!publishableKey || !webhookSecret || (!secretKey && !stripeConfigured)}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        )}

        {stripeConfigured && !editingStripe && (
          <CardContent className="pt-0">
            <div className="text-sm text-[var(--text-secondary)] space-y-1">
              <p>
                <span className="text-[var(--text-muted)]">Publishable Key:</span>{' '}
                {config?.stripe?.publishable_key || 'Not set'}
              </p>
              {config?.stripe?.connect_account_id && (
                <p>
                  <span className="text-[var(--text-muted)]">Connect Account:</span>{' '}
                  {config.stripe.connect_account_id}
                </p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Webhook Configuration - Shows after setup */}
      {setupResult && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <CardTitle className="text-base">Stripe Configured!</CardTitle>
            </div>
            <CardDescription>
              Configure this webhook in your Stripe Dashboard to receive payment events.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-muted)]">Webhook URL</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 text-sm bg-[var(--bg-subtle)] rounded-md font-mono break-all">
                  {setupResult.webhook_url}
                </code>
                <Button variant="ghost" size="sm" onClick={copyWebhookUrl}>
                  {copiedUrl ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-muted)]">Required Events to Enable</Label>
              <div className="flex flex-wrap gap-1">
                {setupResult.required_events.map((event) => (
                  <span
                    key={event}
                    className="px-2 py-0.5 text-xs font-mono bg-[var(--bg-subtle)] rounded"
                  >
                    {event}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <a
                href="https://dashboard.stripe.com/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[var(--accent)] hover:underline flex items-center gap-1"
              >
                Open Stripe Webhooks
                <ExternalLink className="w-3 h-3" />
              </a>
              <Button variant="ghost" size="sm" onClick={() => setSetupResult(null)}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PayPal Card - Coming Soon */}
      <Card className="opacity-60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#003087]/10 flex items-center justify-center">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#003087">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .759-.648h6.264c2.106 0 3.766.52 4.934 1.546 1.234 1.084 1.727 2.68 1.467 4.744-.276 2.188-.98 3.877-2.09 5.016-1.096 1.123-2.683 1.693-4.717 1.693H9.078a.77.77 0 0 0-.759.648l-.887 4.618a.641.641 0 0 1-.356 0z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-base">PayPal</CardTitle>
                <CardDescription>Coming soon</CardDescription>
              </div>
            </div>
            <Button variant="secondary" size="sm" disabled>
              <Key className="w-4 h-4 mr-1" />
              Configure
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-[var(--bg-elevated)]">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-[var(--text-secondary)] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                How payments work
              </p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                When you configure Stripe, your users will be able to purchase subscriptions and one-time products through your apps.
                All payments are processed directly through your Stripe account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
